/**
 * Crowns & Brass, shared by the client and the server. Brass is a server-only ledger currency
 * (never in the save, never sold, never cashed out); a Crown is a numbered legend minted by a
 * scheduled Crown Raid and sold at auction, the price going to the raiders who felled it.
 */

export type EconMode = "off" | "shadow" | "live";

const H = 3_600_000;
const D = 24 * H;

/** Season 1 parameters (see TOKENOMICS.md for why each one is what it is). */
export const ECON = {
  seasonMs: 28 * D,
  /** The last 24h of a season: no bids or buys, so no Brass sits in escrow at the Tide. */
  tideEveMs: D,
  tideFree: 1500,
  tidePct: 50,
  // Supply.
  baseS1: 2400,
  baseFloor: 1200,
  baseCeil: 6000,
  baseStepPct: 25,
  mintPerEarner: 20,
  supplyCapDays: 62,
  recyclePct: 90,
  capOverhangPct: 50,
  acctCapMin: 8,
  acctCapMax: 120,
  clusterCapMult: 1.5,
  /** Raiders must clear min(2%, half a fair share) and come from at least 3 clusters. */
  minSharePct: 2,
  minClusters: 3,
  presenceMs: 5 * 60_000,
  /** The raid pot pays out once per slot (an hour); the daily budget drips out slot by slot. */
  potMs: H,
  cureMintMs: 72 * H,
  cureSpoilsMs: 24 * H,
  // Crown Raids.
  crownIntervalMs: 500 * 60_000,
  crownSirenMs: 10 * 60_000,
  crownLifeMs: 20 * 60_000,
  /** The Crown boss cannot fall before this, so a fast script cannot end the fight alone. */
  crownMinFightMs: 10 * 60_000,
  /**
   * The Crown boss's pool grows with each raider who joins, not with who is online: a raider deals
   * about 180-200 HP a minute with rentals (measured live), so a Crown takes 13-16 minutes whatever
   * the crowd, inside its 20-minute life and past the 10-minute floor.
   */
  crownPoolBase: 3000,
  crownPoolPerRaider: 2400,
  /** The pool grows for at most this many distinct networks (the first damage from each counts). */
  crownPoolMaxGrowth: 12,
  crownLevel: 40,
  crownShinyOdds: 16,
  crownSeasonCap: 84,
  kitDeal: 6,
  kitPick: 3,
  kitLevel: 40,
  kitDrip: 8,
  raidMinTurnMs: 5000,
  /** After a raid session ends (forfeit, wipe, disconnect) the same scout waits this long to rejoin that boss. */
  raidRejoinMs: 10_000,
  // Crown House.
  auctionMs: D,
  openBid: 100,
  raisePct: 5,
  raiseAbs: 10,
  antiSnipeMs: 5 * 60_000,
  feePct: 5,
  spoilsCapPct: 35,
  notaryFee: 25,
  cancelPct: 1,
  cancelMin: 2,
  enshrineFee: 50,
  listingsMax: 4,
  listMin: 10,
  listMax: 1_000_000,
  showcaseSlots: 3,
  // Scout Seal.
  sealAgeMs: 7 * D,
  sealDays: 3,
  sealActiveMin: 20,
  sealRaids: 3,
  newAccountsPerIpDay: 3,
};

/** The six legends Crown Raids rotate through. */
export const CROWN_ROSTER = ["neuralith", "selenarch", "genesiwyrm", "ursarug", "arborealm", "rektquiem"] as const;

export type CosmeticId = "plinth" | "frame" | "plaque" | "trail";
export const COSMETICS: { id: CosmeticId; name: string; price: number; desc: string }[] = [
  { id: "plinth", name: "Gilded Plinth", price: 150, desc: "Your Crown follower walks on a ring of gold." },
  { id: "frame", name: "Crown Frame", price: 250, desc: "Other scouts see a gold frame around your name tag." },
  { id: "plaque", name: "Engraved Plaque", price: 300, desc: "Other scouts see your lead Crown's number after your name." },
  { id: "trail", name: "Starlight Trail", price: 400, desc: "Your Crown follower leaves a trail of stars." },
];

export interface CrownView {
  oid: string;
  label: string;
  species: string;
  level: number;
  shiny: boolean;
  drip: { hp: number; atk: number; def: number; spd: number };
  moves: string[];
  serial: number;
  season: number;
  felledBy: string;
  raiders: number;
  at: number;
  owners: number;
  lastPrice: number | null;
  where: "mine" | "auction" | "listing" | "hall" | "owned";
  verified: boolean;
  /** In the Hall: who enshrined it (null when it came there unsold). */
  enshrinedBy?: string | null;
}

export interface AuctionView {
  id: string;
  crown: CrownView;
  opensIn: number;
  endsIn: number;
  top: { name: string; amount: number; mine: boolean } | null;
  minNext: number;
  /** The viewer's damage share of this Crown, in basis points. */
  myShareBps: number;
  /** What the viewer would be paid if it sold at the current top bid (or the opening bid): after the fee and the 35% cap; 0 if unregistered. */
  myCut: number;
}

export interface CrownListingView {
  id: string;
  crown: CrownView;
  price: number;
  sellerName: string;
  mine: boolean;
}

export interface EconView {
  mode: EconMode;
  season: { n: number; endsIn: number; eve: boolean; seedHash: string } | null;
  wallet: {
    available: number;
    curing: { v: number; until: number }[];
    registered: boolean;
    sealed: boolean;
    seal: { ageDays: number; needAgeDays: number; days: number; needDays: number; raids: number; needRaids: number; adult: boolean; recovery: boolean };
    /** Damage points waiting for this hour's pot payout. */
    pendingPoints: number;
    /** Shadow season: Brass this scout would have earned so far. */
    shadow: number;
  };
  crowns: CrownView[];
  showcase: string[];
  cosmetics: CosmeticId[];
}

export interface CrownHouseView {
  auctions: AuctionView[];
  listings: CrownListingView[];
  /** The newest arrivals in the Hall, plus every Crown the viewer enshrined. */
  hall: CrownView[];
  hallTotal: number;
}

/** Public numbers anyone can audit (served at /ledger). */
export interface LedgerStats {
  mode: EconMode;
  season: number;
  seedHash: string;
  base: number;
  cap: number;
  budgetToday: number;
  mintedToday: number;
  minted: number;
  burned: number;
  circulating: number;
  crowns: number;
  hall: number;
  entries: number;
  head: string;
}

/** What the Raid Gate lends a scout for one raid: six species dealt, three picked. */
export interface RaidKit {
  bossId: string;
  crown: boolean;
  species: string[];
  pick: number;
}

export function sumCuring(w: EconView["wallet"]): number {
  return w.curing.reduce((t, l) => t + l.v, 0);
}
